import React, { useEffect, useState, useMemo } from 'react';
import { Wrench, X, Search, ChevronDown, Loader2 } from 'lucide-react';
import { api } from '../../../lib/api';

/**
 * ToolsFilter — multi-select facetté pour filtrer les candidats par outils.
 *
 * Source : GET /api/admin/candidates/__tools-facets (liste outils + counts)
 * Comportement :
 *   - Affiche le top 30 outils avec leur count
 *   - Recherche locale dans la dropdown
 *   - Multi-select (cumul AND côté backend : doit posséder TOUS les outils sélectionnés)
 *   - Notifie le parent via onChange(["n8n", "Claude"])
 */
const ToolsFilter = ({ value = [], onChange }) => {
  const [open, setOpen] = useState(false);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchFacets = async () => {
      setLoading(true);
      try {
        const res = await api.get('/admin/candidates/__tools-facets');
        if (res.ok && !cancelled) {
          const data = await res.json();
          setTools(data.tools || []);
        }
      } catch (_) { /* silent */ }
      finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchFacets();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tools.slice(0, 60);
    return tools.filter((t) => t.tool.toLowerCase().includes(q)).slice(0, 60);
  }, [tools, search]);

  const toggle = (toolName) => {
    const exists = value.some((v) => v.toLowerCase() === toolName.toLowerCase());
    if (exists) {
      onChange(value.filter((v) => v.toLowerCase() !== toolName.toLowerCase()));
    } else {
      onChange([...value, toolName]);
    }
  };

  const clearAll = () => onChange([]);

  return (
    <div className="relative" data-testid="tools-filter">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm transition-colors ${
          value.length > 0
            ? 'border-tp-accent bg-tp-accent/10 text-tp-primary font-medium'
            : 'border-tp-border bg-white text-tp-text hover:border-tp-primary/40'
        }`}
        data-testid="tools-filter-toggle"
      >
        <Wrench className="h-4 w-4" />
        <span>
          {value.length === 0 ? 'Outil maîtrisé' : `${value.length} outil${value.length > 1 ? 's' : ''}`}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Active chips below button */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5" data-testid="tools-filter-chips">
          {value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 bg-tp-accent/10 text-tp-primary border border-tp-accent/30 px-2 py-0.5 rounded-full text-[11px] font-medium"
            >
              {v}
              <button
                onClick={() => toggle(v)}
                aria-label={`Retirer ${v}`}
                className="hover:text-red-600"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            onClick={clearAll}
            className="text-[11px] text-tp-text-light hover:text-tp-text underline"
          >
            tout effacer
          </button>
        </div>
      )}

      {open && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div className="absolute z-40 mt-1 w-72 max-w-[90vw] bg-white rounded-lg shadow-lg border border-tp-border overflow-hidden">
            <div className="p-2 border-b border-tp-border">
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-tp-text-light" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un outil…"
                  className="w-full pl-8 pr-2 py-1.5 text-sm border border-tp-border rounded focus:outline-none focus:ring-1 focus:ring-tp-accent"
                  autoFocus
                  data-testid="tools-filter-search"
                />
              </div>
            </div>

            <div className="max-h-72 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-6 text-tp-text-light">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : filtered.length === 0 ? (
                <p className="text-xs text-tp-text-light text-center py-4">
                  {tools.length === 0 ? 'Aucun outil détecté en base' : 'Aucun outil correspondant'}
                </p>
              ) : (
                <ul>
                  {filtered.map((t) => {
                    const checked = value.some((v) => v.toLowerCase() === t.tool.toLowerCase());
                    return (
                      <li key={t.tool}>
                        <button
                          type="button"
                          onClick={() => toggle(t.tool)}
                          className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-tp-bg/40 ${
                            checked ? 'bg-tp-accent/5' : ''
                          }`}
                          data-testid={`tools-filter-option-${t.tool}`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <input
                              type="checkbox"
                              checked={checked}
                              readOnly
                              className="h-3.5 w-3.5 accent-tp-accent flex-shrink-0"
                            />
                            <span className="truncate">{t.tool}</span>
                          </span>
                          <span className="text-[11px] text-tp-text-light tabular-nums flex-shrink-0">
                            {t.count}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {value.length > 0 && (
              <div className="border-t border-tp-border p-2 bg-tp-bg/30 flex items-center justify-between">
                <span className="text-xs text-tp-text-light">
                  Filtre AND — doit avoir <strong>tous</strong> les outils
                </span>
                <button
                  onClick={clearAll}
                  className="text-xs text-tp-error hover:underline"
                >
                  Effacer
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ToolsFilter;
